import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useApi, fmtDate } from "@/lib/api";
import { useApp } from "@/lib/app-context";

const label = (n) => {
  if (n.kind !== "coverage_reminder") return n.coverage_name;
  const t = n.coverage_type === "warranty" ? "Warranty" : "Guarantee";
  const when = { 30: "expires in 30 days", 7: "expires in 7 days", 1: "expires tomorrow", 0: "expires today" }[n.offset_days] || "expiring";
  return `${t}${n.coverage_name && n.coverage_name !== t ? ` · ${n.coverage_name}` : ""} ${when}`;
};

export function NotificationBell() {
  const api = useApi();
  const navigate = useNavigate();
  const { me, refreshMe } = useApp();
  const [items, setItems] = useState(null);
  const load = async () => setItems(await api("get", "/notifications").catch(() => []));
  const markRead = async (n) => {
    if (!n.read) await api("post", `/notifications/${n.id}/read`);
    await load();
    refreshMe();
    if (n.kind === "family_joined") navigate("/account?tab=family");
    else if (n.product_id) navigate(n.shared_owner_id ? `/shared/${n.shared_owner_id}/products/${n.product_id}` : `/products/${n.product_id}`);
  };
  const readAll = async () => { await api("post", "/notifications/read-all"); await load(); refreshMe(); };
  const unread = me?.unread_notifications || 0;
  return (
    <Popover onOpenChange={(o) => (o ? load() : setItems(null))}>
      <PopoverTrigger asChild>
        <button className="relative rounded-xl p-2 text-ww-navy transition-colors hover:bg-ww-mint" aria-label="Notifications" data-testid="notification-bell">
          <Bell className="h-5 w-5" />
          {unread > 0 && <span className="absolute right-1 top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-ww-emerald px-1 text-[10px] font-bold text-white" data-testid="notification-unread-count">{unread}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[340px] rounded-2xl border-ww-border p-0" data-testid="notification-panel">
        <div className="flex items-center justify-between border-b border-ww-border px-4 py-3">
          <p className="font-display font-semibold text-ww-navy">Notifications</p>
          <button onClick={readAll} className="flex items-center gap-1 text-xs font-semibold text-ww-emerald" data-testid="notification-mark-all-read"><CheckCheck className="h-3.5 w-3.5" />Mark all read</button>
        </div>
        <div className="max-h-[380px] overflow-y-auto">
          {items === null && <p className="px-4 py-10 text-center text-sm text-ww-muted" data-testid="notification-loading">Loading…</p>}
          {items?.length === 0 && <p className="px-4 py-10 text-center text-sm text-ww-muted" data-testid="notification-empty">No notifications yet. Reminders appear here before coverage expires.</p>}
          {(items || []).map((n) => (
            <button key={n.id} onClick={() => markRead(n)} className={`block w-full border-b border-ww-border/60 px-4 py-3 text-left transition-colors hover:bg-ww-mint/50 ${n.read ? "" : "bg-ww-cyan/60"}`} data-testid={`notification-item-${n.id}`}>
              <div className="flex items-start justify-between gap-2">
                <p className="text-sm font-semibold text-ww-navy">{n.product_name}</p>
                <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${n.read ? "bg-slate-100 text-ww-muted" : "bg-ww-emerald text-white"}`}>{n.read ? "Read" : "New"}</span>
              </div>
              <p className="mt-0.5 text-xs text-ww-muted">{label(n)}</p>
              {n.shared_owner_name && <p className="mt-0.5 text-xs font-medium text-ww-emerald" data-testid={`notification-shared-${n.id}`}>From {n.shared_owner_name}'s wallet</p>}
              {n.expiry_date && <p className="mt-1 text-xs font-medium text-ww-navy/70">Expiry date: {fmtDate(n.expiry_date)}</p>}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
